import React, { useState, useEffect } from 'react'
import { Download, Package, ShoppingCart, X } from 'lucide-react'
import { dexieService } from '../../utils/storage'
import { formatCurrency } from '../../utils/formatters'
import './SelectiveExportModal.scss'

const SelectiveExportModal = ({ isOpen, onClose, products, purchases }) => {
  const [selectedProducts, setSelectedProducts] = useState([])
  const [selectedPurchases, setSelectedPurchases] = useState([])
  const [exportMode, setExportMode] = useState('selective')
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setSelectedProducts([])
      setSelectedPurchases([])
    }
  }, [isOpen])

  const handleExport = async () => {
    setIsExporting(true)

    try {
      const productIds = exportMode === 'all' ? [] : selectedProducts
      const purchaseIds = exportMode === 'all' ? [] : selectedPurchases

      const exportData = await dexieService.exportData(productIds, purchaseIds)

      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json',
      })

      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `export_${exportMode}_${new Date().toISOString().split('T')[0]}.json`
      a.click()
      URL.revokeObjectURL(url)

      onClose()
    } catch (error) {
      console.error('Erreur export:', error)
      alert("Erreur lors de l'export")
    } finally {
      setIsExporting(false)
    }
  }

  const handleProductSelect = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId],
    )
  }

  const handlePurchaseSelect = (purchaseId) => {
    setSelectedPurchases((prev) =>
      prev.includes(purchaseId)
        ? prev.filter((id) => id !== purchaseId)
        : [...prev, purchaseId],
    )
  }

  const toggleAllProducts = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([])
    } else {
      setSelectedProducts(products.map((p) => p.id))
    }
  }

  const toggleAllPurchases = () => {
    if (selectedPurchases.length === purchases.length) {
      setSelectedPurchases([])
    } else {
      setSelectedPurchases(purchases.map((p) => p.id))
    }
  }

  if (!isOpen) return null

  return (
    <div className='modal-overlay'>
      <div className='modal-content lg'>
        <div className='modal-header'>
          <div className='modal-title-row'>
            <h2 className='modal-title'>Export des données</h2>
            <button onClick={onClose} className='btn secondary sm'>
              <X size={16} />
            </button>
          </div>

          <div className='form-group'>
            <div className='form-field'>
              <label>Mode d'export</label>
              <div className='radio-group'>
                <label className='radio-option'>
                  <input
                    type='radio'
                    value='selective'
                    checked={exportMode === 'selective'}
                    onChange={(e) => setExportMode(e.target.value)}
                  />
                  Export sélectif
                </label>
                <label className='radio-option'>
                  <input
                    type='radio'
                    value='all'
                    checked={exportMode === 'all'}
                    onChange={(e) => setExportMode(e.target.value)}
                  />
                  Export complet
                </label>
              </div>
            </div>
          </div>

          {exportMode === 'selective' && (
            <div className='selection-content'>
              <div className='selection-section'>
                <div className='section-header'>
                  <h3>
                    <Package size={20} /> Produits ({products.length})
                  </h3>
                  <button
                    onClick={toggleAllProducts}
                    className='btn sm secondary'
                  >
                    {selectedProducts.length === products.length
                      ? 'Tout désélectionner'
                      : 'Tout sélectionner'}
                  </button>
                </div>
                <div className='items-list'>
                  {products.map((product) => (
                    <label key={product.id} className='item-checkbox'>
                      <input
                        type='checkbox'
                        checked={selectedProducts.includes(product.id)}
                        onChange={() => handleProductSelect(product.id)}
                      />
                      <div className='item-info'>
                        <div className='item-name'>{product.name}</div>
                        <div className='item-details'>
                          {product.code} • {formatCurrency(product.basePrice)}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className='selection-section'>
                <div className='section-header'>
                  <h3>
                    <ShoppingCart size={20} /> Achats ({purchases.length})
                  </h3>
                  <button
                    onClick={toggleAllPurchases}
                    className='btn sm secondary'
                  >
                    {selectedPurchases.length === purchases.length
                      ? 'Tout désélectionner'
                      : 'Tout sélectionner'}
                  </button>
                </div>
                <div className='items-list'>
                  {purchases.map((purchase) => (
                    <label key={purchase.id} className='item-checkbox'>
                      <input
                        type='checkbox'
                        checked={selectedPurchases.includes(purchase.id)}
                        onChange={() => handlePurchaseSelect(purchase.id)}
                      />
                      <div className='item-info'>
                        <div className='item-name'>{purchase.clientName}</div>
                        <div className='item-details'>
                          {formatCurrency(purchase.totalAmount)}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className='export-summary'>
            <div className='summary-text'>
              Export de{' '}
              {exportMode === 'all' ? products.length : selectedProducts.length}{' '}
              produit(s) et{' '}
              {exportMode === 'all'
                ? purchases.length
                : selectedPurchases.length}{' '}
              achat(s)
            </div>
          </div>

          <div className='form-row pt-4'>
            <button onClick={onClose} className='btn secondary flex-1'>
              Annuler
            </button>
            <button
              onClick={handleExport}
              className='btn primary flex-1'
              disabled={
                isExporting ||
                (exportMode === 'selective' &&
                  selectedProducts.length === 0 &&
                  selectedPurchases.length === 0)
              }
            >
              <Download size={16} className='btn-icon' />
              {isExporting ? 'Export...' : 'Exporter'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SelectiveExportModal
